import { Button, Grid } from '@mui/material';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { getValueInStorage, setValueInStorage } from '../../services/storage-service';
import { LOCAL_STORAGE, WIDGET_COMPONENT_MAPPING } from '../../utils/constants';
import { Responsive, WidthProvider } from 'react-grid-layout';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import "./Dashboard.css"

const ResponsiveGridLayout = WidthProvider(Responsive);

type Props = {
	selectedDashboard: any;
};

const fullHeightWidgets = ['AGENT_LIST', 'AGENT_LIST_V2', 'AGENT_CURRENT_STATE_MULTI', 'CAROUSEL', 'CALL_CENTER_WALLBOARD', 'CALL_CENTER_WALLBOARD_V2', 'AGENT_LIST_CIRCULAR', 'WFM_DASHBOARD'];

function Dashboard({ selectedDashboard }: Props) {
	const navigate = useNavigate();
	const localSelectedDashboard = getValueInStorage(LOCAL_STORAGE.SELECTED_DASHBOARD);
	selectedDashboard = selectedDashboard?.id ? selectedDashboard : localSelectedDashboard || {};
	const [rowHeight, setRowHeight] = React.useState(200);
	React.useEffect(() => {
		if (!selectedDashboard?.id) {
			navigate('/preset');
		}
		if (selectedDashboard?.id && !localSelectedDashboard?.id) {
			setValueInStorage(LOCAL_STORAGE.SELECTED_DASHBOARD, selectedDashboard);
		}
	}, []);
	const layouts = selectedDashboard?.dashboard?.layouts?.lg || [];
	const generateKey = (layout: any, idx: number) => {
		return idx + (layout.i) + (layout.x) + (layout.y) + (layout.w) + (layout.h) + (layout.widgetName) + (selectedDashboard?.id ?? '');
	}

	const [isFullscreen, setIsFullScreen] = React.useState<boolean>(false);
	const contentRef: any = React.useRef(null)

	const handleFullScreenChange = () => {
		try {
			const fullScreenElement = document.fullscreenElement;
			setIsFullScreen(!!fullScreenElement);
		} catch (error) {
			console.warn("Error checking full screen state", error);
			setIsFullScreen(false);
		}
	};

	// Handle fullscreen change events
	React.useEffect(() => {
		// Add event listener for full screen change
		document.addEventListener('fullscreenchange', handleFullScreenChange);
		document.addEventListener('webkitRequestFullscreen', handleFullScreenChange);
		document.addEventListener('msRequestFullscreen', handleFullScreenChange);

		// Clean up function to remove event listener when the component unmounts
		return () => {
			document.removeEventListener('fullscreenchange', handleFullScreenChange);
			document.removeEventListener('webkitRequestFullscreen', handleFullScreenChange);
			document.removeEventListener('msRequestFullscreen', handleFullScreenChange);
		};
	}, []);

	// Toggle fullscreen
	const handleFullScreen = () => {
		try {
			const element = contentRef?.current;
			if (element.requestFullscreen) {
				element.requestFullscreen();
			} else if (element.webkitRequestFullscreen) {
				/* Safari */
				element.webkitRequestFullscreen();
			} else if (element.msRequestFullscreen) {
				/* IE11 */
				element.msRequestFullscreen();
			}
		} catch (error) {
			console.warn("Unable to enter full screen mode", error);
		}
	};



	React.useEffect(() => {
		const updateRowHeight = () => {
			try {
				if (layouts.length === 1 && fullHeightWidgets.includes(layouts[0].widgetName)) {
					const height = document.documentElement.clientHeight;
					let innerHeight = window.innerHeight;
					if (isFullscreen) {
						setRowHeight(height * 0.31);
						return
					}
					if (innerHeight > 1250) {
						setRowHeight(height * 0.30);
					} else if (innerHeight > 980) {
						setRowHeight(height * 0.29);
					}
					else if (innerHeight < 980 && innerHeight >= 920) {
						setRowHeight(height * 0.287);
					}
					else if (innerHeight < 920 && innerHeight > 800) {
						setRowHeight(height * 0.28);
					}
					else {
						setRowHeight(height * 0.27);
					}
				}
			} catch (error) {
				console.error(error);
				setRowHeight(200)
			}
		};
		updateRowHeight();
		window.addEventListener("resize", updateRowHeight);
		return () => window.removeEventListener("resize", updateRowHeight);
	}, [selectedDashboard, isFullscreen]);

	return (
		<>
			<Grid container spacing={0} gap={0} direction="row" justifyContent="space-between" alignItems="center">
				<Grid item>
					{/* <span className="tv-monitor-heading">TV Monitor</span> */}
				</Grid>
				<Grid item mr={1}>
					<Button onClick={handleFullScreen} variant="outlined" size='small' sx={{ height: '25px' }} startIcon={<FullscreenIcon />}>
						Full screen
					</Button>
				</Grid>
			</Grid>

			<div className="oz-dashboard-main-container" style={isFullscreen ? { padding: '5px', paddingRight: '0px', overflow: 'scroll', backgroundColor: '#f6fafd' } : {}} ref={contentRef}>
				<ResponsiveGridLayout
					className="dashboard-layout"
					layouts={layouts}
					rowHeight={rowHeight}
					isDraggable={false}
					cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
					breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
				>
					{layouts.map((layout: any, idx: number) => {
						const WidgetToRender = WIDGET_COMPONENT_MAPPING[layout.widgetName];
						if (!WidgetToRender) return null;
						return (
							<div
								key={generateKey(layout, idx)}
								data-grid={{ x: layout.x, y: layout.y, w: layout.w, h: layout.h }}
							>
								<div className='widget-view' style={{ height: '100%', width: '100%', padding: '0px' }}>
									<WidgetToRender
										{...(layout?.widgetProps || {})}
										selectedFilters={layout?.widgetFilters || {}}
										selectedThresholds={layout?.widgetThresholds || {}}
										widgetId={layout?.i}
										widgetData={layout?.widgetData || {}}
										fullscreenContainer={contentRef.current}
										isFullscreen={isFullscreen}
									/>
								</div>
							</div>
						);
					})}

				</ResponsiveGridLayout>
			</div>
		</>
	);
}

export default Dashboard;
